import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, FileText, ExternalLink, Eye, EyeOff, Calendar as CalendarIcon, BellOff, Split as SplitIcon, Rocket, Zap, Hourglass, TrendingUp, TrendingDown, Megaphone, Globe } from 'lucide-react';
import { dismissDivChangeAlert, DIV_CHANGE_EVENT } from './DivChangeToast';

// Round P157 — notification kinds that are NOT SEC filing rows (each
// gets its own bell section; everything else renders under SEC Filings).
function DismissBtn({ onDismiss, testId }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onDismiss(); }}
      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      title="Dismiss this alert only — your watchlist and portfolio are untouched."
      aria-label="Dismiss alert"
      data-testid={testId}
    >
      Dismiss
    </button>
  );
}

const NON_FILING_KINDS = ['dividend_payment', 'dividend_declared', 'wht_reclaim', 'squeeze_alert', 'spac_vote_alert', 'warrant_expiry_alert', 'minervini_stage2_alert'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 92n++ — Category chip definitions. Keys MUST match
// CATEGORY_LABELS in backend/server.py (earnings, leadership, ma,
// material, other). The hint tooltips show the underlying SEC Item
// codes so users can map labels back to filings they see in the
// wild. Order matches the conceptual signal-to-noise gradient.
// `dot` is the Tailwind class for the per-row colored chip badge.
const CATEGORY_DEFS = [
  { key: 'earnings',   label: 'Earnings',   hint: 'Item 2.02 — Results of Operations & Financial Condition',
    dot: 'border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300' },
  { key: 'leadership', label: 'Leadership', hint: 'Item 5.02 — Director/Officer departures, appointments, comp changes',
    dot: 'border-sky-300/60 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300' },
  { key: 'ma',         label: 'M&A',        hint: 'Items 1.01, 1.02, 2.01 — Material agreements, terminations, acquisitions/dispositions',
    dot: 'border-violet-300/60 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300' },
  { key: 'material',   label: 'Material',   hint: 'Items 2.03, 2.04, 2.05, 2.06, 8.01 — Debt, impairments, restructuring, other material events',
    dot: 'border-amber-300/60 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300' },
  { key: 'other',      label: 'Other',      hint: 'All other 8-K Items (Regulation FD, exhibits, routine disclosures)',
    dot: 'border-zinc-300/60 dark:border-zinc-700/60 bg-zinc-50/60 dark:bg-zinc-900/30 text-zinc-600 dark:text-zinc-400' },
];

const CATEGORY_BY_KEY = Object.fromEntries(CATEGORY_DEFS.map((c) => [c.key, c]));

// Mirror of backend ITEM_TO_CATEGORY in /app/backend/server.py. Keep
// in sync: a code missing from this map falls through to "other".
const ITEM_TO_CATEGORY = {
  'Item 1.01': 'ma',
  'Item 2.01': 'ma',
  'Item 2.02': 'earnings',
  'Item 5.02': 'leadership',
  'Item 8.01': 'material',
};

// Priority order when a single filing touches multiple categories —
// pick the most signal-rich one for the row badge. Earnings/M&A
// almost always trump a routine leadership note.
const CATEGORY_PRIORITY = ['earnings', 'ma', 'material', 'leadership', 'other'];

function dominantCategory(itemCodes) {
  if (!Array.isArray(itemCodes) || itemCodes.length === 0) return 'other';
  const cats = new Set(itemCodes.map((c) => ITEM_TO_CATEGORY[c] || 'other'));
  return CATEGORY_PRIORITY.find((p) => cats.has(p)) || 'other';
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * FilingAlertsBell — May 2026.
 *
 * Dashboard pill that polls /api/notifications/unread-count every 60s
 * and shows a popover of recent 8-K material events for tickers the
 * user owns OR watchlists. Backed by the daily 6 AM ET filing-alerts
 * cron in services/dividend_scheduler.py.
 *
 * Quiet by default — no badge when there's nothing new. The popover
 * stays mounted but lazy-loads `/api/notifications/list` only when the
 * user actually opens it. "Mark all read" zeroes the unread count
 * without removing the items (history is preserved for reference).
 */
export default function FilingAlertsBell({ user, onOpenStock, idSuffix = '' }) {
  // Round 92o++++++++N++++++++ — Paywall proxy. Until the billing /
  // `useTier()` context ships, the SEC filing alerts bell is
  // admin-only as a stand-in for "Plus" subscribers. Swap this gate
  // for `useTier().isPlus` when billing goes live.
  const isAdmin = user && user.role === 'admin';
  // Round 93t — Ex-Div alerts are available to EVERY logged-in user.
  // Bell now renders for any signed-in user; SEC section stays gated
  // by `isAdmin`. If the paywall is removed in the future, just unify
  // these (e.g., delete `isAdmin` and let everyone see both).
  const isLoggedIn = !!(user && user.user_id);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [mutedCategories, setMutedCategories] = useState([]);  // Round 92n++ — array now
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  // Round 93t — Ex-Div alert list state. Independent of SEC filings.
  const [exdivAlerts, setExdivAlerts] = useState([]);
  const [exdivLoaded, setExdivLoaded] = useState(false);
  // Round 96z (Jul 2026) — Split alert list state. Auto-populated
  // (no user opt-in) — every logged-in user sees upcoming splits for
  // their portfolio + watchlist. Independent bell section.
  const [splitAlerts, setSplitAlerts] = useState([]);
  const [splitLoaded, setSplitLoaded] = useState(false);
  // Round 98n (Jul 2026) — IPO alert list state. Feeds a dedicated
  // "IPO Watch" section in the bell dropdown. Independent from ex-div
  // and split alerts — driven by user prefs on /ipo-watch page.
  const [ipoAlerts, setIpoAlerts] = useState([]);
  const [ipoLoaded, setIpoLoaded] = useState(false);
  // Sep 2026 — Dividend Cuts / Raises on held stocks (same feed as the dashboard toast).
  const [divChangeAlerts, setDivChangeAlerts] = useState([]);
  // Round 96ab (Jul 2026) — bell dropdown filter chip. Lets the user
  // narrow the visible sections to one alert kind. Purely client-side.
  const [bellFilter, setBellFilter] = useState('all'); // 'all' | 'splits' | 'exdiv' | 'paid' | 'filings' | 'ipo'
  // Feb 2026 — "Mark all reviewed" state. Stores stable IDs of alerts
  // the user has already scanned so the yellow bell state resets
  // WITHOUT wiping the underlying rows (data still scrollable in the
  // dropdown; new alerts with un-reviewed IDs re-light the bell).
  // Distinct from SEC's `markAllRead` (which flips per-row `read_at`
  // via a backend call) — this one is client-side because IPO / ex-div
  // / split alerts have no server-side read/unread concept.
  const REVIEWED_KEY = 'dividrip:bell-reviewed';
  const [reviewedIds, setReviewedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(REVIEWED_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });
  const popoverRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    // Round 96d (Jul 2026) — bell unread count is now available to
    // ALL logged-in users, not just admin. Non-admin users see only
    // their dividend_payment rows (client-side filter below); admin
    // sees dividend_payment + filing_8k. This lets every user get
    // "Payment received" bell alerts even before the SEC-filings
    // paywall is unified.
    if (!isLoggedIn) { setUnread(0); return; }
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnread(Number(r.data?.unread || 0));
    } catch {
      /* silent */
    }
  }, [isLoggedIn]);

  const fetchList = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const r = await axios.get(`${API}/notifications/list?limit=20`, { withCredentials: true });
      const raw = Array.isArray(r.data?.notifications) ? r.data.notifications : [];
      // Non-admin: surface dividend_payment + squeeze_alert + SPAC
      // vote + warrant expiry rows (filing_8k remains behind the
      // paywall / admin-only gate).
      const filtered = isAdmin ? raw : raw.filter((n) => NON_FILING_KINDS.includes(n.kind));
      setItems(filtered);
      setLoaded(true);
    } catch {
      setItems([]);
      setLoaded(true);
    }
  }, [isLoggedIn, isAdmin]);

  // Round 93t — Ex-Div alert fetch. Available to ALL logged-in users
  // (no admin gate). Uses the same /api/exdiv-alerts/upcoming endpoint
  // that powers the popup modal so the bell stays perfectly in sync
  // with what the popup would show.
  const fetchExdiv = useCallback(async () => {
    if (!isLoggedIn) { setExdivAlerts([]); setExdivLoaded(true); return; }
    try {
      const r = await axios.get(`${API}/exdiv-alerts/upcoming`, { withCredentials: true });
      setExdivAlerts(Array.isArray(r.data?.alerts) ? r.data.alerts : []);
      setExdivLoaded(true);
    } catch {
      setExdivAlerts([]);
      setExdivLoaded(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchUnread();
    if (!isLoggedIn) return;
    const id = setInterval(fetchUnread, 60_000);
    return () => clearInterval(id);
  }, [fetchUnread, isLoggedIn]);

  // Round 93t — Ex-Div alerts refresh every 5 minutes. Lighter than
  // SEC's 60s poll because alert state changes only at midnight ET
  // or when the user adds a new position.
  useEffect(() => {
    fetchExdiv();
    if (!isLoggedIn) return;
    const id = setInterval(fetchExdiv, 5 * 60_000);
    return () => clearInterval(id);
  }, [fetchExdiv, isLoggedIn]);

  // Round 96z — Split alert fetcher. Same 5-min cadence as ex-div.
  const fetchSplits = useCallback(async () => {
    if (!isLoggedIn) { setSplitAlerts([]); setSplitLoaded(true); return; }
    try {
      const r = await axios.get(`${API}/alerts/splits`, { withCredentials: true });
      // Bell shows ALL non-removed alerts (even ones dismissed from
      // the banner). Banner filters to `visible=true` on its own.
      setSplitAlerts(Array.isArray(r.data?.alerts) ? r.data.alerts : []);
      setSplitLoaded(true);
    } catch {
      setSplitAlerts([]);
      setSplitLoaded(true);
    }
  }, [isLoggedIn]);
  useEffect(() => {
    fetchSplits();
    if (!isLoggedIn) return;
    const id = setInterval(fetchSplits, 5 * 60_000);
    return () => clearInterval(id);
  }, [fetchSplits, isLoggedIn]);

  // Round 98n (Jul 2026) — IPO alerts fetch. Also refreshes on the
  // custom 'ipo-alerts-prefs-changed' event dispatched by
  // IpoAlertSettings so a settings change here in the bell reflects
  // instantly instead of waiting for the 5-min poll.
  const fetchIpo = useCallback(async () => {
    if (!isLoggedIn) { setIpoAlerts([]); setIpoLoaded(true); return; }
    try {
      const r = await axios.get(`${API}/ipo-alerts/upcoming`, { withCredentials: true });
      setIpoAlerts(Array.isArray(r.data?.alerts) ? r.data.alerts : []);
      setIpoLoaded(true);
    } catch {
      setIpoAlerts([]);
      setIpoLoaded(true);
    }
  }, [isLoggedIn]);
  useEffect(() => {
    fetchIpo();
    if (!isLoggedIn) return;
    const id = setInterval(fetchIpo, 5 * 60_000);
    const onPrefsChange = () => fetchIpo();
    window.addEventListener('ipo-alerts-prefs-changed', onPrefsChange);
    return () => {
      clearInterval(id);
      window.removeEventListener('ipo-alerts-prefs-changed', onPrefsChange);
    };
  }, [fetchIpo, isLoggedIn]);

  const fetchDivChange = useCallback(async () => {
    if (!isLoggedIn) { setDivChangeAlerts([]); return; }
    try {
      const r = await axios.get(`${API}/journal/dividend-cut-alerts`, { withCredentials: true });
      setDivChangeAlerts(Array.isArray(r.data?.alerts) ? r.data.alerts : []);
    } catch {
      setDivChangeAlerts([]);
    }
  }, [isLoggedIn]);
  useEffect(() => {
    fetchDivChange();
    if (!isLoggedIn) return;
    const id = setInterval(fetchDivChange, 5 * 60_000);
    window.addEventListener(DIV_CHANGE_EVENT, fetchDivChange);
    return () => {
      clearInterval(id);
      window.removeEventListener(DIV_CHANGE_EVENT, fetchDivChange);
    };
  }, [fetchDivChange, isLoggedIn]);

  // Load notification prefs once per session. Returns the user's
  // muted_categories array (empty = all 8-Ks reach the bell). The
  // backend also returns a legacy `high_signal_only` boolean for
  // back-compat, which it auto-normalises into the array if set.
  useEffect(() => {
    if (!isAdmin || prefsLoaded) return;
    let cancelled = false;
    axios.get(`${API}/notifications/prefs`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setMutedCategories(Array.isArray(r.data?.muted_categories) ? r.data.muted_categories : []);
        setPrefsLoaded(true);
      })
      .catch(() => { if (!cancelled) setPrefsLoaded(true); });
    return () => { cancelled = true; };
  }, [isAdmin, prefsLoaded]);

  const toggleCategory = async (cat) => {
    const next = mutedCategories.includes(cat)
      ? mutedCategories.filter((c) => c !== cat)
      : [...mutedCategories, cat];
    setMutedCategories(next);  // optimistic
    try {
      await axios.post(`${API}/notifications/prefs`, { muted_categories: next }, { withCredentials: true });
    } catch {
      // Revert on failure
      setMutedCategories(mutedCategories);
    }
  };

  // Lazy-load list on first open
  useEffect(() => {
    if (open && !loaded) {
      fetchList();
    }
  }, [open, loaded, fetchList]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!isLoggedIn) return null;

  // Round 93t — Combined unread count for the badge: SEC unread (admin
  // only) + count of upcoming ex-div alerts + split alerts + IPO
  // alerts. Ex-div / split / IPO alerts are "fresh" by definition
  // (auto-clear after the event / user dismissal), so they count even
  // without a per-row read/unread state — bell should be yellow
  // whenever the dropdown has any content, grey only when empty.
  //
  // Feb 2026 — "Mark all reviewed" subtracts alerts whose stable IDs
  // are in `reviewedIds`. Bell re-lights when NEW alerts arrive with
  // IDs not in the reviewed set. `unread` (SEC filings) is handled by
  // the existing `markAllRead` path and stays zero after that click,
  // so we simply add unmarked-reviewed counts on top.
  const alertKeyIpo = (a) => `ipo:${a.ticker}`;
  const alertKeyExdiv = (a) => `exdiv:${a.ticker}-${a.ex_div_date}`;
  const alertKeySplit = (a) => `split:${a.id}`;
  const alertKeyDivChange = (a) => `divchg:${a.ticker}-${a.last_date}`;
  const unreviewed = (arr, keyFn) => arr.filter((a) => !reviewedIds.has(keyFn(a))).length;
  const totalUnread = unread
    + unreviewed(exdivAlerts, alertKeyExdiv)
    + unreviewed(splitAlerts, alertKeySplit)
    + unreviewed(divChangeAlerts, alertKeyDivChange)
    + unreviewed(ipoAlerts, alertKeyIpo);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setUnread(0);
      setItems((arr) => arr.map((it) => ({ ...it, read_at: it.read_at || new Date().toISOString() })));
    } catch {
      /* silent */
    }
  };

  // Feb 2026 — Mark every currently-visible alert as "reviewed" so the
  // bell drops back to grey without deleting any rows. Merges the new
  // IDs into the existing reviewedIds set and persists to localStorage.
  // Also flips SEC unread to zero (for admin) by calling the existing
  // /notifications/mark-all-read endpoint — so this one button clears
  // both the client-side (IPO/exdiv/split) and server-side (SEC) sides
  // in a single click without conflicting with the standalone
  // "Mark all read" button which admins still see for SEC-only zeroing.
  const markAllReviewed = async () => {
    const nextSet = new Set(reviewedIds);
    ipoAlerts.forEach((a) => nextSet.add(alertKeyIpo(a)));
    exdivAlerts.forEach((a) => nextSet.add(alertKeyExdiv(a)));
    splitAlerts.forEach((a) => nextSet.add(alertKeySplit(a)));
    divChangeAlerts.forEach((a) => nextSet.add(alertKeyDivChange(a)));
    setReviewedIds(nextSet);
    try {
      localStorage.setItem(REVIEWED_KEY, JSON.stringify(Array.from(nextSet)));
    } catch { /* swallow */ }
    // If SEC unread items exist (admin only), zero them via the same
    // endpoint the standalone "Mark all read" button uses. Non-admins
    // have `unread === 0` so this is a no-op for them.
    if (unread > 0) {
      try {
        await markAllRead();
      } catch { /* silent */ }
    }
  };

  // Round 92o++++++++N++++++++++ (May 2026) — user-requested. Wipes
  // every filing-alert row for this user so the bell dropdown empties.
  // The 6 AM ET filing-alerts cron dedupes by accession via a separate
  // `filing_alerts_seen` collection, so already-scanned 8-Ks won't
  // reappear — only genuinely new filings will repopulate.
  const clearAll = async () => {
    try {
      await axios.post(`${API}/notifications/clear-all`, {}, { withCredentials: true });
      setItems([]);
      setUnread(0);
      setLoaded(true);
    } catch {
      /* silent */
    }
  };

  // Sep 2026 — every bell row is dismissable. Removes the alert only;
  // watchlist / portfolio are never touched.
  // The row leaves the list at once, but the server delete waits 5 s so
  // a mis-tap can be undone from the toast.
  const dismissItem = (it) => {
    setItems((arr) => arr.filter((x) => x.accession !== it.accession));
    if (!it.read_at) setUnread((n) => Math.max(0, n - 1));
    let undone = false;
    const timer = setTimeout(async () => {
      if (undone) return;
      try {
        await axios.post(`${API}/notifications/${encodeURIComponent(it.accession)}/dismiss`, {}, { withCredentials: true });
      } catch {
        fetchList();
      }
    }, 5000);
    toast(`Dismissed ${it.ticker || 'alert'}`, {
      description: 'Alert removed — watchlist and portfolio untouched.',
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          setItems((arr) => (arr.some((x) => x.accession === it.accession) ? arr : [it, ...arr]));
          if (!it.read_at) setUnread((n) => n + 1);
        },
      },
    });
  };

  const onItemClick = async (it) => {
    // Mark this single item read (server is idempotent on already-read)
    if (!it.read_at) {
      try {
        await axios.post(`${API}/notifications/${encodeURIComponent(it.accession)}/mark-read`, {}, { withCredentials: true });
        setUnread((n) => Math.max(0, n - 1));
        setItems((arr) => arr.map((x) => x.accession === it.accession ? { ...x, read_at: new Date().toISOString() } : x));
      } catch {
        /* silent */
      }
    }
    if (onOpenStock && it.ticker) {
      // Round 92o++++++++N++++++++ — Pass accession so the consumer
      // can deep-link into the new "10K/8K/Risks" tab and pulse the
      // matching 8-K row for ~3 seconds.
      onOpenStock(it.ticker, it.accession);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={popoverRef} data-testid="filing-alerts-bell">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
          totalUnread > 0
            ? 'text-yellow-500 hover:text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/15'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
        }`}
        title={totalUnread > 0
          ? (() => {
              // Round P157 — the server `unread` counter spans ALL
              // notification kinds; subtract the non-filing kinds we
              // can see in the fetched list so the tooltip doesn't
              // label dividend/squeeze/SPAC/warrant alerts as "SEC
              // filings" (approximate: list is capped at 20 rows).
              const nonFilingUnread = items.filter((n) => NON_FILING_KINDS.includes(n.kind) && !n.read_at).length;
              const otherAlertUnread = items.filter((n) => NON_FILING_KINDS.includes(n.kind) && n.kind !== 'dividend_payment' && !n.read_at).length;
              const filingsUnread = Math.max(0, unread - nonFilingUnread);
              return `${totalUnread} alert${totalUnread === 1 ? '' : 's'}: ${exdivAlerts.length} ex-div${divChangeAlerts.length > 0 ? `, ${divChangeAlerts.length} cut/raise${divChangeAlerts.length === 1 ? '' : 's'}` : ''}${splitAlerts.length > 0 ? `, ${splitAlerts.length} split${splitAlerts.length === 1 ? '' : 's'}` : ''}${ipoAlerts.length > 0 ? `, ${ipoAlerts.length} IPO${ipoAlerts.length === 1 ? '' : 's'}` : ''}${otherAlertUnread > 0 ? `, ${otherAlertUnread} stock alert${otherAlertUnread === 1 ? '' : 's'}` : ''}${isAdmin && filingsUnread > 0 ? `, ${filingsUnread} SEC filing${filingsUnread === 1 ? '' : 's'}` : ''}`;
            })()
          : 'Alerts'}
        aria-label={`Alerts (${totalUnread} unread)`}
        data-testid={`filing-alerts-bell-toggle${idSuffix}`}
      >
        <Bell className={`w-4 h-4 ${totalUnread > 0 ? 'fill-yellow-500/30' : ''}`} />
        {totalUnread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-yellow-500 text-black text-[9px] font-bold inline-flex items-center justify-center"
            data-testid="filing-alerts-unread-badge"
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-[320px] sm:w-[380px] max-h-[480px] rounded-lg border bg-popover shadow-lg z-50 flex flex-col"
          data-testid="filing-alerts-popover"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
            <div className="flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5 text-muted-foreground" />
              <h3 className="text-xs font-bold uppercase tracking-wide">Alerts</h3>
            </div>
            <div className="flex items-center gap-2">
              {totalUnread > 0 && (
                <button
                  type="button"
                  onClick={markAllReviewed}
                  className="text-[11px] text-muted-foreground hover:text-foreground font-semibold"
                  title="Zero the yellow bell count without deleting any rows. New alerts will re-light the bell."
                  data-testid="filing-alerts-mark-all-reviewed"
                >
                  Mark reviewed
                </button>
              )}
              {isAdmin && unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] text-muted-foreground hover:text-foreground font-semibold"
                  data-testid="filing-alerts-mark-all-read"
                >
                  Mark all read
                </button>
              )}
              {isAdmin && items.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[11px] text-rose-600 dark:text-rose-400 hover:text-rose-500 dark:hover:text-rose-300 font-semibold"
                  title="Delete every SEC filing alert from this dropdown. New 8-K filings will repopulate at the next 6 AM ET scan."
                  data-testid="filing-alerts-clear-all"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Round 96ab (Jul 2026) — Kind filter chip row. Purely a
                client-side view filter so the user can focus on one
                alert kind at a time. "All" is the default and shows
                every populated section. Shown whenever ≥2 distinct
                sections have entries so filtering is meaningful. */}
            {(() => {
              const paidCount = items.filter((n) => n.kind === 'dividend_payment').length;
              const filingsCount = items.filter((n) => !NON_FILING_KINDS.includes(n.kind)).length;
              const populatedSections = [
                splitAlerts.length > 0,
                ipoAlerts.length > 0,
                exdivAlerts.length > 0,
                divChangeAlerts.length > 0,
                paidCount > 0,
                isAdmin && filingsCount > 0,
              ].filter(Boolean).length;
              if (populatedSections < 2) return null;
              const chips = [
                { key: 'all', label: 'All', hide: false },
                { key: 'splits', label: `Splits (${splitAlerts.length})`, hide: splitAlerts.length === 0 },
                { key: 'ipo', label: `IPO (${ipoAlerts.length})`, hide: ipoAlerts.length === 0 },
                { key: 'exdiv', label: `Ex-div (${exdivAlerts.length})`, hide: exdivAlerts.length === 0 },
                { key: 'divchange', label: `Cuts/Raises (${divChangeAlerts.length})`, hide: divChangeAlerts.length === 0 },
                { key: 'paid', label: `Paid (${paidCount})`, hide: paidCount === 0 },
                { key: 'filings', label: `Filings (${filingsCount})`, hide: !isAdmin || filingsCount === 0 },
              ].filter((c) => !c.hide);
              return (
                <div
                  className="flex items-center gap-1 px-3 py-1.5 border-b border-border overflow-x-auto whitespace-nowrap sticky top-0 z-10 bg-popover"
                  data-testid="bell-filter-row"
                >
                  {chips.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setBellFilter(c.key)}
                      className={`shrink-0 ${
                        bellFilter === c.key
                          ? 'rounded-full text-[10px] font-bold px-2 py-0.5 bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-500/40'
                          : 'rounded-full text-[10px] font-semibold px-2 py-0.5 border border-border text-muted-foreground hover:text-foreground hover:border-violet-500/40 transition-colors'
                      }`}
                      data-testid={`bell-filter-${c.key}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              );
            })()}
            {/* Round 96z (Jul 2026) — Upcoming Splits section.
                Auto-populated: every logged-in user sees splits for
                their portfolio + watchlist. No opt-in. Sits ABOVE
                ex-div because splits are time-critical corporate
                actions (share count, options strikes, cost basis
                all change on the ex-date). Self-hides when empty. */}
            {splitLoaded && splitAlerts.length > 0 && (bellFilter === 'all' || bellFilter === 'splits') && (
              <>
                <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-1.5" data-testid="filing-alerts-splits-header">
                  <SplitIcon className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    Upcoming Splits ({splitAlerts.length})
                  </span>
                </div>
                <ul className="divide-y divide-border" data-testid="filing-alerts-splits-list">
                  {splitAlerts.map((s) => {
                    const dismissed = s.banner_dismissed_until && s.banner_dismissed_until > new Date().toISOString();
                    const handleClick = () => {
                      if (onOpenStock && s.ticker) {
                        onOpenStock(s.ticker);
                        setOpen(false);
                      }
                    };
                    const handleRemove = async (e) => {
                      e.stopPropagation();
                      setSplitAlerts((arr) => arr.filter((x) => x.id !== s.id));
                      try {
                        await axios.post(`${API}/alerts/splits/${s.id}/remove`, {}, { withCredentials: true });
                      } catch {
                        fetchSplits();
                      }
                    };
                    return (
                      <li
                        key={s.id}
                        className="px-3 py-2 hover:bg-muted/40 cursor-pointer transition-colors"
                        onClick={handleClick}
                        data-testid={`split-bell-row-${s.ticker}`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="font-mono text-xs font-bold text-foreground">{s.ticker}</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300">
                            {s.display_ratio}
                          </span>
                          {dismissed && (
                            <span className="text-[9px] font-semibold text-muted-foreground" title={`Banner hidden until ${s.banner_dismissed_until.slice(0,10)}`}>
                              banner dismissed
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={handleRemove}
                            className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10"
                            title="Remove this split alert entirely."
                            data-testid={`split-bell-remove-${s.ticker}`}
                          >
                            REMOVE
                          </button>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {s.adjustment_type === 'reverse_split' ? 'Reverse split' : 'Forward split'} on {s.execution_date}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {/* Round 98n (Jul 2026) — IPO Watch Alerts section. Only
                surfaces if the user has enabled alerts on /ipo-watch.
                Self-hides when empty. Each row has a per-ticker
                DISMISS button that persists via
                POST /api/ipo-alerts/dismiss/{ticker}. Dismissals
                auto-clear whenever the user changes any filter on
                the IPO Watch page (server-enforced). */}
            {ipoLoaded && ipoAlerts.length > 0 && (bellFilter === 'all' || bellFilter === 'ipo') && (
              <>
                <div className="px-3 py-1.5 bg-violet-500/10 border-b border-violet-500/30 flex items-center gap-1.5" data-testid="filing-alerts-ipo-header">
                  <Rocket className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                    New IPOs ({ipoAlerts.length})
                  </span>
                </div>
                <ul className="divide-y divide-border" data-testid="filing-alerts-ipo-list">
                  {ipoAlerts.map((ipo) => {
                    const handleClick = () => {
                      if (onOpenStock && ipo.ticker) {
                        onOpenStock(ipo.ticker);
                        setOpen(false);
                      }
                    };
                    const handleDismiss = async (e) => {
                      e.stopPropagation();
                      setIpoAlerts((arr) => arr.filter((x) => x.ticker !== ipo.ticker));
                      try {
                        await axios.post(`${API}/ipo-alerts/dismiss/${encodeURIComponent(ipo.ticker)}`, {}, { withCredentials: true });
                      } catch {
                        fetchIpo();  // rollback on failure
                      }
                    };
                    return (
                      <li
                        key={ipo.ticker}
                        className="px-3 py-2 hover:bg-muted/40 cursor-pointer transition-colors"
                        onClick={handleClick}
                        data-testid={`ipo-bell-row-${ipo.ticker}`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="font-mono text-xs font-bold text-foreground">{ipo.ticker}</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-violet-300/60 dark:border-violet-800/60 bg-violet-50/40 dark:bg-violet-950/20 text-violet-700 dark:text-violet-300">
                            {ipo.kind}
                          </span>
                          {ipo.is_upcoming && (
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300"
                              title="Not yet listed"
                              data-testid={`ipo-bell-upcoming-${ipo.ticker}`}
                            >
                              UPCOMING
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={handleDismiss}
                            className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10"
                            title="Dismiss this IPO alert. Change your window setting to re-surface."
                            data-testid={`ipo-bell-dismiss-${ipo.ticker}`}
                          >
                            DISMISS
                          </button>
                        </div>
                        <p className="text-[10px] text-muted-foreground line-clamp-1" title={ipo.issuer_name || ''}>
                          {ipo.issuer_name || '—'} · listed {ipo.listing_date || '—'}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {/* Sep 2026 — Dividend Cuts / Raises on held stocks. Mute = "don't show
                again" for THAT change only (server-side, keyed by change date). */}
            {divChangeAlerts.length > 0 && (bellFilter === 'all' || bellFilter === 'divchange') && (
              <>
                <div className="px-3 py-1.5 bg-rose-50/40 dark:bg-rose-950/20 border-b border-border flex items-center gap-1.5" data-testid="div-change-alerts-header">
                  <TrendingDown className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    Dividend Cuts / Raises ({divChangeAlerts.length})
                  </span>
                </div>
                <ul className="divide-y divide-border" data-testid="div-change-alerts-list">
                  {divChangeAlerts.map((a) => {
                    const isCut = a.kind !== 'raise';
                    const pct = a.change_pct != null ? `${a.change_pct > 0 ? '+' : ''}${Number(a.change_pct).toFixed(1)}%` : '';
                    const fmt = (v) => (v != null ? `$${Number(v).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}` : null);
                    const handleMute = async (e) => {
                      e.stopPropagation();
                      setDivChangeAlerts((arr) => arr.filter((x) => !(x.ticker === a.ticker && x.last_date === a.last_date)));
                      try {
                        await dismissDivChangeAlert(a);
                      } catch {
                        fetchDivChange();
                      }
                    };
                    return (
                      <li
                        key={`${a.ticker}-${a.last_date}`}
                        className="px-3 py-2 hover:bg-muted/40 cursor-pointer transition-colors"
                        onClick={() => { if (onOpenStock && a.ticker) { onOpenStock(a.ticker); setOpen(false); } }}
                        data-testid={`div-change-bell-row-${a.ticker}`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="font-mono text-xs font-bold text-foreground">{a.ticker}</span>
                          {isCut
                            ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" data-testid={`div-change-cut-tag-${a.ticker}`}>CUT {pct}</span>
                            : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" data-testid={`div-change-raise-tag-${a.ticker}`}>RAISE {pct}</span>}
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded border border-blue-300/60 dark:border-blue-800/60 bg-blue-50/60 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">OWNED</span>
                          <button
                            type="button"
                            onClick={handleMute}
                            className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 hover:bg-muted/60 transition-colors"
                            title={`Mute this ${isCut ? 'cut' : 'raise'} on ${a.ticker} — a later change will alert again`}
                            data-testid={`div-change-bell-mute-${a.ticker}`}
                          >
                            <BellOff className="w-2.5 h-2.5" />
                            Mute
                          </button>
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-snug line-clamp-1" title={a.name || ''}>
                          {a.name}
                          {fmt(a.prev_amount) && fmt(a.last_amount) && (
                            <> · <span className="font-mono">{fmt(a.prev_amount)} → {fmt(a.last_amount)}</span></>
                          )}
                          {a.last_date && <> · {new Date(a.last_date.slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {/* Round 93t — Ex-Div Alerts section. Always visible for
                logged-in users. Self-hides when empty AND admin SEC
                section has content (otherwise the empty state is the
                only thing to show). Renders ABOVE the SEC list. */}
            {exdivLoaded && (exdivAlerts.length > 0 || !isAdmin) && (bellFilter === 'all' || bellFilter === 'exdiv') && (
              <>
                <div className="px-3 py-1.5 bg-muted/30 border-b border-border flex items-center gap-1.5" data-testid="filing-alerts-exdiv-header">
                  <CalendarIcon className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    Upcoming Ex-Div ({exdivAlerts.length})
                  </span>
                </div>
                {exdivAlerts.length === 0 ? (
                  <div className="px-3 py-4 text-center">
                    <p className="text-[11px] text-muted-foreground font-semibold mb-1">No upcoming ex-div alerts.</p>
                    <p className="text-[10px] text-muted-foreground/70 leading-relaxed font-semibold">
                      Pick your lead-time windows on the Dividend Calendar page to start receiving alerts.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-border" data-testid="filing-alerts-exdiv-list">
                    {exdivAlerts.map((a) => {
                      const tone = a.days_until_ex <= 1
                        ? 'border-rose-300/60 dark:border-rose-800/60 bg-rose-50/40 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300'
                        : a.days_until_ex <= 3
                          ? 'border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300'
                          : 'border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300';
                      const sourceColor = a.source === 'watchlist'
                        ? 'border-purple-300/60 dark:border-purple-800/60 bg-purple-50/60 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300'
                        : 'border-blue-300/60 dark:border-blue-800/60 bg-blue-50/60 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300';
                      const handleClick = () => {
                        if (onOpenStock && a.ticker) {
                          onOpenStock(a.ticker);
                          setOpen(false);
                        }
                      };
                      const handleMute = async (e) => {
                        e.stopPropagation();
                        // Optimistic UI — drop the row instantly so
                        // the click feels snappy. If the server call
                        // fails we'll re-fetch and the row will pop
                        // back.
                        setExdivAlerts((arr) => arr.filter((x) => x.ticker !== a.ticker));
                        try {
                          await axios.post(
                            `${API}/exdiv-alerts/mute-ticker`,
                            { ticker: a.ticker, days: 1 },
                            { withCredentials: true },
                          );
                        } catch {
                          // Re-fetch on failure to restore the row.
                          fetchExdiv();
                        }
                      };
                      const urgencyLabel = a.days_until_ex === 0
                        ? 'TODAY'
                        : a.days_until_ex === 1
                          ? 'TOMORROW'
                          : `${a.days_until_ex}D`;
                      return (
                        <li
                          key={`${a.ticker}-${a.ex_div_date}`}
                          className="px-3 py-2 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={handleClick}
                          data-testid={`exdiv-bell-row-${a.ticker}`}
                        >
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className="font-mono text-xs font-bold text-foreground">{a.ticker}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tone}`}>
                              EX-DIV {urgencyLabel}
                            </span>
                            <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${sourceColor}`} title={a.source === 'watchlist' ? 'On your watchlist' : 'In your portfolio'}>
                              {a.source === 'watchlist' ? 'WATCHED' : 'OWNED'}
                            </span>
                            {/* Round 93u — Per-ticker "mute for today"
                                action. Stops row-click propagation so
                                the user doesn't get yanked into the
                                stock modal when they meant to silence
                                the row. Hidden behind hover/focus on
                                desktop, always visible on mobile via
                                the persistent opacity baseline. */}
                            <button
                              type="button"
                              onClick={handleMute}
                              className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 hover:bg-muted/60 transition-colors"
                              title={`Mute ${a.ticker} ex-div alerts until midnight ET`}
                              data-testid={`exdiv-bell-mute-${a.ticker}`}
                            >
                              <BellOff className="w-2.5 h-2.5" />
                              Mute
                            </button>
                          </div>
                          <p className="text-[10px] text-muted-foreground leading-snug">
                            Ex-Div <span className="font-mono font-semibold text-amber-700 dark:text-amber-300">
                              {new Date(a.ex_div_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                            {a.pay_date && (
                              <>
                                {' · Pay '}
                                <span className="font-mono font-semibold text-green-700 dark:text-green-400">
                                  {new Date(a.pay_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </span>
                              </>
                            )}
                            {a.estimated_total != null && (
                              <>
                                {' · ~'}
                                <span className="font-mono font-bold text-green-700 dark:text-green-400">
                                  ${Number(a.estimated_total).toFixed(2)}
                                </span>
                              </>
                            )}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}

            {/* Round 96d (Jul 2026) — Payments Received section.
                Visible to ALL logged-in users (dividend_payment rows
                aren't gated behind the SEC-filings paywall). Renders
                between the ex-div block and the admin SEC filings
                block. Self-hides when no payment rows exist so it
                never shows an empty header for non-admin users who
                haven't received any dividends yet. */}
            {(() => {
              const paymentRows = items.filter((it) => it.kind === 'dividend_payment');
              if (paymentRows.length === 0) return null;
              if (bellFilter !== 'all' && bellFilter !== 'paid') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-emerald-50/40 dark:bg-emerald-950/20 border-b border-border flex items-center gap-1.5" data-testid="filing-alerts-payment-header">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[8px] font-bold">$</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                      Payments Received ({paymentRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="filing-alerts-payment-list">
                    {paymentRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`filing-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300">
                              PAID
                            </span>
                            <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                              {Number(it.amount) >= 0 ? `$${Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                            </span>
                            {!it.read_at && (
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-label="unread" />
                            )}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && (
                          <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Round P147·2 (Jun 2026) — Squeeze Watch section. Fed by
                the daily 6:30 AM ET cron that compares the two most
                recent FINRA short-interest settlements for every
                watchlisted ticker (alert when SI jumped ≥25%). Visible
                to ALL logged-in users; one alert max per ticker per
                bi-weekly settlement cycle. Clicking opens the stock
                modal so the user can check the Short Interest & Float
                card. */}
            {(() => {
              const squeezeRows = items.filter((it) => it.kind === 'squeeze_alert');
              if (squeezeRows.length === 0) return null;
              if (bellFilter !== 'all') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-rose-50/40 dark:bg-rose-950/20 border-b border-border flex items-center gap-1.5" data-testid="squeeze-alerts-header">
                    <Zap className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                      Squeeze Watch ({squeezeRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="squeeze-alerts-list">
                    {squeezeRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-rose-50/40 dark:hover:bg-rose-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`squeeze-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 bg-rose-50/60 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300">
                              SQUEEZE
                            </span>
                            {it.jump_pct != null && (
                              <span className="text-[10px] font-bold text-rose-700 dark:text-rose-300 font-mono">
                                SI +{Number(it.jump_pct).toFixed(0)}%
                              </span>
                            )}
                            {!it.read_at && (
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" aria-label="unread" />
                            )}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && (
                          <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Sep 2026 — Minervini Stage-2 entrants: a watched/held non-div
                name newly passed all 8 Trend Template rules overnight. */}
            {/* Sep 2026 — Dividend Declared: Massive published the next payout amount. */}
            {(() => {
              const declRows = items.filter((it) => it.kind === 'dividend_declared');
              if (declRows.length === 0 || bellFilter !== 'all') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-emerald-50/40 dark:bg-emerald-950/20 border-b border-border flex items-center gap-1.5" data-testid="declared-alerts-header">
                    <Megaphone className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                      Dividend Declared ({declRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="declared-alerts-list">
                    {declRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`declared-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="font-mono text-xs font-bold text-emerald-700 dark:text-emerald-300">${Number(it.per_share).toFixed(4)}</span>
                            {it.direction === 'raise' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" data-testid="declared-raise-tag">RAISE +{it.change_pct}%</span>}
                            {it.direction === 'cut' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" data-testid="declared-cut-tag">CUT {it.change_pct}%</span>}
                            {it.direction === 'unchanged' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground">UNCHANGED</span>}
                            {!it.read_at && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-label="unread" />}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Oct 2026 — Foreign withholding reclaim reminder after an ADR payout
                from a country that withholds above the US treaty rate. */}
            {(() => {
              const whtRows = items.filter((it) => it.kind === 'wht_reclaim');
              if (whtRows.length === 0 || bellFilter !== 'all') return null;
              const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              return (
                <>
                  <div className="px-3 py-1.5 bg-amber-50/40 dark:bg-amber-950/20 border-b border-border flex items-center gap-1.5" data-testid="wht-reclaim-alerts-header">
                    <Globe className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      Withholding Reclaim ({whtRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="wht-reclaim-alerts-list">
                    {whtRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-amber-50/40 dark:hover:bg-amber-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`wht-reclaim-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="text-[10px] text-muted-foreground">{it.country}</span>
                            {it.reclaimable >= 0.01 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" data-testid="wht-reclaim-tag">RECLAIM {money(it.reclaimable)}</span>}
                            {it.lost_retirement >= 0.01 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" data-testid="wht-lost-tag">IRA LOST {money(it.lost_retirement)}</span>}
                            {!it.read_at && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-label="unread" />}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && <p className="text-[11px] text-foreground/85 leading-snug line-clamp-4">{it.preview}</p>}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {(() => {
              const s2Rows = items.filter((it) => it.kind === 'minervini_stage2_alert');
              if (s2Rows.length === 0) return null;
              if (bellFilter !== 'all') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-sky-50/40 dark:bg-sky-950/20 border-b border-border flex items-center gap-1.5" data-testid="stage2-alerts-header">
                    <TrendingUp className="w-3 h-3 text-sky-600 dark:text-sky-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                      Stage 2 · Minervini ({s2Rows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="stage2-alerts-list">
                    {s2Rows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-sky-50/40 dark:hover:bg-sky-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`stage2-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            {it.direction === 'exit' ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" data-testid="stage2-exit-tag">
                                BROKE TEMPLATE
                              </span>
                            ) : it.direction === 'tight_base' ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" data-testid="stage2-tight-tag">
                                TIGHT BASE
                              </span>
                            ) : it.direction === 'sepa' ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" data-testid="stage2-sepa-tag">
                                SEPA LITE PASS
                              </span>
                            ) : (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-sky-300/60 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300">
                                {it.sepa_pass ? 'NEW · SEPA LITE' : 'NEW · TREND TEMPLATE'}
                              </span>
                            )}
                            {it.rs_rank != null && <span className="text-[10px] font-bold text-sky-700 dark:text-sky-300 font-mono">RS {it.rs_rank}</span>}
                            {it.tight_base && it.direction !== 'tight_base' && <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">Tight base</span>}
                            {!it.read_at && <span className="w-1.5 h-1.5 rounded-full bg-sky-500" aria-label="unread" />}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Round P157 — SPAC merger-vote alerts. Fanned out daily to
                holders + watchers when a SPAC's lifecycle stage hits
                vote_pending (merger proxy on file → redemption deadline
                approaching). Clicking opens the stock modal. */}
            {(() => {
              const voteRows = items.filter((it) => it.kind === 'spac_vote_alert');
              if (voteRows.length === 0) return null;
              if (bellFilter !== 'all') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-violet-50/40 dark:bg-violet-950/20 border-b border-border flex items-center gap-1.5" data-testid="spac-vote-alerts-header">
                    <Rocket className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                      SPAC Vote ({voteRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="spac-vote-alerts-list">
                    {voteRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-violet-50/40 dark:hover:bg-violet-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`spac-vote-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-violet-300/60 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300">
                              VOTE PENDING
                            </span>
                            {!it.read_at && (
                              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" aria-label="unread" />
                            )}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && (
                          <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Round P157 — Warrant expiry / redemption alerts for
                holders + watchers (expiring ≤45 days or issuer filed a
                redemption notice). */}
            {(() => {
              const warrantRows = items.filter((it) => it.kind === 'warrant_expiry_alert');
              if (warrantRows.length === 0) return null;
              if (bellFilter !== 'all') return null;
              return (
                <>
                  <div className="px-3 py-1.5 bg-amber-50/40 dark:bg-amber-950/20 border-b border-border flex items-center gap-1.5" data-testid="warrant-expiry-alerts-header">
                    <Hourglass className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      Warrant Expiry ({warrantRows.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-border" data-testid="warrant-expiry-alerts-list">
                    {warrantRows.map((it) => (
                      <li
                        key={it.accession}
                        className={`px-3 py-2 hover:bg-amber-50/40 dark:hover:bg-amber-950/20 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                        onClick={() => onItemClick(it)}
                        data-testid={`warrant-expiry-alert-${it.accession}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">
                              {it.alert_type === 'redemption' ? 'REDEMPTION' : 'EXPIRING'}
                            </span>
                            {it.alert_type !== 'redemption' && it.days_left != null && (
                              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 font-mono">
                                {it.days_left}d left
                              </span>
                            )}
                            {!it.read_at && (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-label="unread" />
                            )}
                          </div>
                          <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                        </div>
                        {it.preview && (
                          <p className="text-[11px] text-foreground/85 leading-snug line-clamp-3">{it.preview}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* SEC filing alerts section — admin/Plus only */}
            {isAdmin && (bellFilter === 'all' || bellFilter === 'filings') && (
              <>
                <div className="px-3 py-1.5 bg-muted/30 border-b border-border flex items-center gap-1.5" data-testid="filing-alerts-sec-header">
                  <FileText className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    SEC Filings ({items.filter((it) => !NON_FILING_KINDS.includes(it.kind)).length})
                  </span>
                </div>
                {!loaded ? (
                  <p className="px-3 py-6 text-xs text-muted-foreground text-center">Loading…</p>
                ) : items.filter((it) => !NON_FILING_KINDS.includes(it.kind)).length === 0 ? (
                  <div className="px-3 py-6 text-center">
                    <p className="text-xs text-muted-foreground mb-1 font-semibold">No recent filings.</p>
                    <p className="text-[10px] text-muted-foreground/70 leading-relaxed font-semibold">
                      We scan daily at 6 AM ET for 8-K material events on stocks you own or watchlist.
                    </p>
                  </div>
                ) : (
              <ul className="divide-y divide-border" data-testid="filing-alerts-list">
                {items.filter((it) => !NON_FILING_KINDS.includes(it.kind)).map((it) => {
                  // Round 96d (Jul 2026) — dividend_payment rows are
                  // rendered above in the standalone Payments Received
                  // block; this SEC section is now filing_8k only.
                  const catKey = dominantCategory(it.item_codes);
                  const cat = CATEGORY_BY_KEY[catKey] || CATEGORY_BY_KEY.other;
                  const catMuted = mutedCategories.includes(catKey);
                  return (
                  <li
                    key={it.accession}
                    className={`px-3 py-2 hover:bg-muted/40 cursor-pointer transition-colors ${it.read_at ? 'opacity-70' : ''}`}
                    onClick={() => onItemClick(it)}
                    data-testid={`filing-alert-${it.accession}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="font-mono text-xs font-bold text-foreground">{it.ticker}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">
                          {it.form_type || '8-K'}
                        </span>
                        {/* Round 92n++ — per-row category badge. Click
                            mutes/unmutes that category instantly. The
                            chip stops row-click propagation so users
                            don't get yanked into the stock modal when
                            they meant to silence a topic. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleCategory(catKey); }}
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border transition-colors ${
                            catMuted
                              ? 'border-border bg-muted/40 text-muted-foreground line-through opacity-70 hover:opacity-90'
                              : cat.dot
                          }`}
                          title={catMuted ? `Unmute ${cat.label} alerts` : `Mute ${cat.label} alerts (${cat.hint})`}
                          data-testid={`filing-alert-cat-${it.accession}`}
                          aria-pressed={!catMuted}
                        >
                          {cat.label}
                        </button>
                        {it.is_owned && (
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300" title="You own this stock">
                            OWNED
                          </span>
                        )}
                        {!it.is_owned && it.is_watched && (
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded border border-sky-300/60 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300" title="On your watchlist">
                            WATCHED
                          </span>
                        )}
                        {!it.read_at && (
                          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" aria-label="unread" />
                        )}
                      </div>
                      <span className="flex items-center gap-1.5 shrink-0"><span className="text-[10px] text-muted-foreground">{timeAgo(it.created_at)}</span><DismissBtn onDismiss={() => dismissItem(it)} testId={`bell-dismiss-${it.accession}`} /></span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap mb-1">
                      {(it.item_codes || []).slice(0, 4).map((c) => (
                        <span key={c} className="text-[10px] font-semibold text-muted-foreground border border-border rounded px-1 py-0.5">
                          {c}
                        </span>
                      ))}
                    </div>
                    {it.preview && (
                      <p className="text-[11px] text-foreground/85 leading-snug line-clamp-2">{it.preview}</p>
                    )}
                  </li>
                  );
                })}
              </ul>
                )}
              </>
            )}
          </div>
          {/* Round 93t — Categories prefs footer is SEC-specific.
              Hidden for non-admin users since they only see ex-div
              alerts (governed by the Calendar page settings). */}
          {isAdmin && (
            <div className="px-3 py-2 border-t bg-muted/20" data-testid="filing-alerts-prefs">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Categories</p>
              {items.length > 0 && unread > 0 && (
                <Eye className="w-3 h-3 text-yellow-500" title={`${unread} unread`} />
              )}
              {items.length > 0 && unread === 0 && (
                <EyeOff className="w-3 h-3 text-muted-foreground" title="All read" />
              )}
            </div>
            {/* Round 92n++ — 5 category chips replacing the single
                high-signal boolean. Active (green) = alerts flow.
                Muted (faint, line-through) = alerts skipped. Each
                chip tooltips its underlying SEC Item code(s) so users
                can map the label back to filings they see in the wild.
                Hold-and-buy income investors typically keep Earnings
                + M&A + Material on, mute Leadership and Other. */}
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_DEFS.map(({ key, label, hint, dot }) => {
                const muted = mutedCategories.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleCategory(key)}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
                      muted
                        ? 'border-border bg-muted/40 text-muted-foreground line-through opacity-70 hover:opacity-90'
                        : dot
                    }`}
                    title={hint}
                    data-testid={`filing-alerts-cat-${key}`}
                    aria-pressed={!muted}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
